import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "react-query";
import { DEFAULT_PER_PAGE } from "../../../helpers/default";
import ContentBlock from "../../../components/Layout/ContentBlock";
import FilterMenu from "../../../components/Layout/FilterMenu";
import {
    ButtonSecondary,
} from "../../../components/globals";
import SwitchComp from "../../../components/common/SwitchComp";
import {
    antdAsyncMessageError,
    antdAsyncMessageSuccess,
} from "../../../helpers/AsyncMessage";
import axios from "axios";
import {
    IconDocNumber,
    IconWarehouse,
    IconWarehouse2
} from "../../../components/Layout/Icons";
import { CheckBoxInput } from "../../../components/common/Buttons";
import TableUi from "../../../components/Layout/TableUI";
import PaginationComp from "../../../components/Dashboard/items/Pagination";
import { MdDateRange, MdEdit, MdCheck, MdClose, MdErrorOutline } from "react-icons/md";
import { FaTrash } from "react-icons/fa";
import { Tooltip, Modal, Select } from "antd";
import { paginate } from "../../../helpers/Utils";
import dayjs from "dayjs";
import AntdConfirm from "../../../helpers/AntdConfirm";
import DropdownButton from "../../products/components/SuppliersManagement/Components/DropdownButton";

const DocumentDetail = ({ documentId, onBack, onRefresh }) => {
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(DEFAULT_PER_PAGE);
    const [selected, setSelected] = useState([]);
    const [localDeletedIds, setLocalDeletedIds] = useState([]);
    const [transferModalOpen, setTransferModalOpen] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [departmentsLoading, setDepartmentsLoading] = useState(false);
    const [transferSubmitting, setTransferSubmitting] = useState(false);
    const [transferToId, setTransferToId] = useState(null);
    const [transferSourceByItemId, setTransferSourceByItemId] = useState({});

    const selectedIdSet = useMemo(() => new Set((selected || []).map((id) => String(id))), [selected]);
    const localDeletedIdSet = useMemo(() => new Set((localDeletedIds || []).map((id) => String(id))), [localDeletedIds]);

    // Filter states
    const [filterVVSku, setFilterVVSku] = useState("");
    const [filterSupplierCode, setFilterSupplierCode] = useState("");
    const [filterTitle, setFilterTitle] = useState("");
    const [filterCategory, setFilterCategory] = useState("");
    const [filterBrand, setFilterBrand] = useState("");
    const [filterStatus, setFilterStatus] = useState("");

    const fetchDocumentDetail = async () => {
        try {
            const response = await axios.get(`/api/v1/product_transfers/${documentId}`);
            const data = response.data;

            const activeItems = (data.product_transfer_items || []).filter(item => !item.deleted_at);
            const allProductsConfirmed = activeItems.length > 0 &&
                activeItems.every(item => item.confirmed_at);

            const hasSuccessfulShipment = (data.shipments || []).some(s =>
                s.tracking_numbers?.length > 0 ||
                (s.shipment_type?.toLowerCase() === 'vv_transport' && !s.api_errors)
            );

            const document = {
                id: data.id,
                document_no: data.document_number,
                supplier_order_no: data.inner_document_number || "-",
                arrival_date: data.created_at,
                sender: data.sender?.title || data.sender?.code || data.sender?.name || "-",
                sender_id: data.sender?.id || null,
                sender_code: data.sender?.code || "-",
                receiver: data.receiver?.title || data.receiver?.code || data.receiver?.name || "-",
                receiver_id: data.receiver?.id || null,
                total_products: data.product_transfer_items?.length || 0,
                status: data.status,
                manager: data.manager,
                inner_document_number: data.inner_document_number,
                invoice_number: data.invoice_number,
                all_products_confirmed: allProductsConfirmed,
                hasSuccessfulShipment: hasSuccessfulShipment,
                active_item_ids: activeItems.map(item => item.id),
            };

            let items = (data.product_transfer_items || []).map(item => ({
                id: item.id,
                product_id: item.product_id || item.product?.id || null,
                bb_sku: item.product?.sku || "-",
                vv_sku: item.product?.vv_sku || "-",
                reserve_number: item.reserve_number,
                reserve_error: item.reserve_error,
                product_suppliers: item.product?.product_suppliers || [],
                supplier_code: "-",
                title: item.product?.title || "-",
                category: "-",
                brand: "-",
                cost_price: item.prime_cost || "-",
                qty: item.quantity,
                available_qty: item.available_qty,
                status: item.confirmed_at ? "Confirmed" : "Not confirmed",
                user_confirmed: !!item.confirmed_at,
                confirmed_by: item.confirmed_by,
                deleted_at: item.deleted_at
            }));

            if (filterVVSku) {
                items = items.filter(item =>
                    item.vv_sku.toLowerCase().includes(filterVVSku.toLowerCase())
                );
            }
            if (filterSupplierCode) {
                items = items.filter(item =>
                    item.supplier_code.toLowerCase().includes(filterSupplierCode.toLowerCase())
                );
            }
            if (filterTitle) {
                items = items.filter(item =>
                    item.title.toLowerCase().includes(filterTitle.toLowerCase())
                );
            }
            if (filterCategory) {
                items = items.filter(item =>
                    item.category.toLowerCase().includes(filterCategory.toLowerCase())
                );
            }
            if (filterBrand) {
                items = items.filter(item =>
                    item.brand.toLowerCase().includes(filterBrand.toLowerCase())
                );
            }
            if (filterStatus) {
                items = items.filter(item => {
                    if (filterStatus === "confirmed") {
                        return item.status === "Confirmed";
                    } else if (filterStatus === "not_confirmed") {
                        return item.status === "Not confirmed";
                    }
                    return true;
                });
            }

            return {
                document,
                items,
                total: items.length
            };
        } catch (error) {
            console.error('Error fetching document detail:', error);
            return {
                document: {},
                items: [],
                total: 0
            };
        }
    };

    const {
        data: documentData,
        isLoading: loading,
        error: documentError,
        refetch: refetchDocument,
    } = useQuery([`vv_stock_document_${documentId}`, page, pageCount, filterVVSku, filterSupplierCode, filterTitle, filterCategory, filterBrand, filterStatus], fetchDocumentDetail);

    const document = documentData?.document;
    const items = documentData?.items || [];

    const mutationInner = useMutation({
        mutationFn: (id) => {
            return axios.post(`/api/v1/product_transfers/${id}/create_inner`);
        },
        onSuccess: (response, id) => {
            if (response.data?.success === false) {
                antdAsyncMessageError({
                    text: response.data?.message || "Failed to create internal document",
                    key: "create_inner",
                });
                refetchDocument();
                return;
            }
            antdAsyncMessageSuccess({
                text: "Internal document created successfully",
                key: "create_inner",
            });
            refetchDocument();
            if (onRefresh && typeof onRefresh === 'function') {
                onRefresh();
            }
        },
        onError: (error) => {
            antdAsyncMessageError({
                text: error?.response?.data?.message || "Failed to create internal document",
                key: "create_inner",
            });
            refetchDocument();
        },
    });

    const mutationInvoice = useMutation({
        mutationFn: (id) => {
            return axios.post(`/api/v1/product_transfers/${id}/create_invoice`);
        },
        onSuccess: (response, id) => {
            if (response.data?.success === false) {
                antdAsyncMessageError({
                    text: response.data?.message || "Failed to create invoice",
                    key: "create_invoice",
                });
                refetchDocument();
                return;
            }
            antdAsyncMessageSuccess({
                text: "Invoice created successfully",
                key: "create_invoice",
            });
            refetchDocument();
            if (onRefresh && typeof onRefresh === 'function') {
                onRefresh();
            }
        },
        onError: (error) => {
            antdAsyncMessageError({
                text: error?.response?.data?.message || "Failed to create invoice",
                key: "create_invoice",
            });
            refetchDocument();
        },
    });

    const handleFilterChange = () => {
        refetchDocument();
    };

    const handleFilterClear = () => {
        setFilterVVSku("");
        setFilterSupplierCode("");
        setFilterTitle("");
        setFilterCategory("");
        setFilterBrand("");
        setFilterStatus("");
    };

    const handleConfirmToggle = async (itemId, currentStatus) => {
        if (document?.inner_document_number) {
            antdAsyncMessageError({
                text: "Cannot change product status after internal document is created",
                key: "confirm_item",
            });
            return;
        }

        try {
            const endpoint = currentStatus
                ? `/api/v1/product_transfers/${documentId}/unconfirm_item`
                : `/api/v1/product_transfers/${documentId}/confirm_item`;

            const response = await axios.post(endpoint, {
                product_transfer_item_id: itemId
            });

            if (response.data?.success === false) {
                antdAsyncMessageError({
                    text: response.data?.error || "Error updating confirmation",
                    key: "confirm_item",
                });
                refetchDocument();
                return;
            }

            antdAsyncMessageSuccess({
                text: currentStatus ? "Item unconfirmed" : "Item confirmed",
                key: "confirm_item",
            });
            refetchDocument();
        } catch (err) {
            antdAsyncMessageError({
                text: err?.response?.data?.error || "Error updating confirmation",
                key: "confirm_item",
            });
            refetchDocument();
        }
    };

    const allActiveConfirmed = useMemo(() => {
        if (!items || items.length === 0) return false;
        const activeItems = items.filter(item => !item.deleted_at && !localDeletedIdSet.has(String(item.id)));
        return activeItems.length > 0 && activeItems.every(item => item.user_confirmed);
    }, [items, localDeletedIdSet]);

    const handleBulkConfirmToggle = async () => {
        if (document?.inner_document_number) {
            antdAsyncMessageError({
                text: "Cannot change product status after internal document is created",
                key: "confirm_item",
            });
            return;
        }

        const activeItems = items.filter(item => !item.deleted_at && !localDeletedIdSet.has(String(item.id)));
        const shouldConfirm = !allActiveConfirmed;

        const itemIds = shouldConfirm
            ? activeItems.filter(item => !item.user_confirmed).map(item => item.id)
            : activeItems.filter(item => item.user_confirmed).map(item => item.id);

        if (itemIds.length === 0) return;

        const endpoint = shouldConfirm
            ? `/api/v1/product_transfers/${documentId}/confirm_item`
            : `/api/v1/product_transfers/${documentId}/unconfirm_item`;

        try {
            const response = await axios.post(endpoint, {
                product_transfer_item_id: itemIds
            });

            if (response.data?.success === false) {
                antdAsyncMessageError({
                    text: response.data?.error || "Error updating confirmations",
                    key: "confirm_item",
                });
                refetchDocument();
                return;
            }

            antdAsyncMessageSuccess({
                text: shouldConfirm ? "All items confirmed" : "All items unconfirmed",
                key: "confirm_item",
            });
            refetchDocument();
        } catch (err) {
            antdAsyncMessageError({
                text: err?.response?.data?.error || "Error updating confirmations",
                key: "confirm_item",
            });
            refetchDocument();
        }
    };

    const handleBulkConfirmSelected = async (shouldConfirm) => {
        if (selected.length === 0) return;
        if (document?.inner_document_number) {
            antdAsyncMessageError({
                text: "Cannot change product status after internal document is created",
                key: "confirm_item",
            });
            return;
        }
        const selectedItems = items.filter(item => selectedIdSet.has(String(item.id)) && !item.deleted_at && !localDeletedIdSet.has(String(item.id)));

        const itemIds = shouldConfirm
            ? selectedItems.filter(item => !item.user_confirmed).map(item => item.id)
            : selectedItems.filter(item => item.user_confirmed).map(item => item.id);

        if (itemIds.length === 0) return;

        const endpoint = shouldConfirm
            ? `/api/v1/product_transfers/${documentId}/confirm_item`
            : `/api/v1/product_transfers/${documentId}/unconfirm_item`;

        try {
            const response = await axios.post(endpoint, {
                product_transfer_item_id: itemIds
            });

            if (response.data?.success === false) {
                antdAsyncMessageError({
                    text: response.data?.error || "Error updating confirmations",
                    key: "confirm_item",
                });
                refetchDocument();
                return;
            }

            antdAsyncMessageSuccess({
                text: shouldConfirm ? "Selected items confirmed" : "Selected items unconfirmed",
                key: "confirm_item",
            });
            refetchDocument();
        } catch (err) {
            antdAsyncMessageError({
                text: err?.response?.data?.error || err?.message || "Error updating confirmations",
                key: "confirm_item",
            });
            refetchDocument();
        }
    };

    const handleBulkDelete = async () => {
        if (selected.length === 0) return;
        try {
            for (const itemId of selected) {
                await axios.post(`/api/v1/product_transfers/${documentId}/delete_item`, {
                    product_transfer_item_id: Number(itemId) || itemId
                });
            }
            antdAsyncMessageSuccess({
                text: `${selected.length} item(s) deleted`,
                key: "bulk_delete",
            });
            const newDeletedIds = [...localDeletedIds, ...selected.map((id) => Number(id) || id)];
            setLocalDeletedIds(newDeletedIds);
            setSelected([]);

            const newDeletedIdSet = new Set(newDeletedIds.map((id) => String(id)));
            const activeItemIds = document?.active_item_ids || [];
            const allItemsDeleted = activeItemIds.length > 0 && activeItemIds.every((id) =>
                newDeletedIdSet.has(String(id))
            );

            if (allItemsDeleted && document?.status !== 'canceled') {
                await handleStatusChange('canceled');
            } else {
                refetchDocument();
            }
        } catch (err) {
            antdAsyncMessageError({
                text: err?.response?.data?.error || "Error deleting items",
                key: "bulk_delete",
            });
            refetchDocument();
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            const response = await axios.post(`/api/v1/product_transfers/${documentId}/set_status`, {
                product_transfer: {
                    status: newStatus
                }
            });

            if (response.data?.success === false) {
                antdAsyncMessageError({
                    text: response.data?.error || "Error updating status",
                    key: "status_change",
                });
                refetchDocument();
                return;
            }

            antdAsyncMessageSuccess({
                text: `Transfer ${newStatus === 'completed' ? 'completed' : 'canceled'} successfully`,
                key: "status_change",
            });

            await refetchDocument();

            if (onRefresh && typeof onRefresh === 'function') {
                onRefresh();
            }
        } catch (err) {
            antdAsyncMessageError({
                text: err?.response?.data?.error || "Error updating status",
                key: "status_change",
            });
            refetchDocument();
        }
    };

    const handleCreateInternalDocument = () => {
        if (!document?.all_products_confirmed) {
            antdAsyncMessageError({
                text: "All products must be confirmed before creating internal document",
                key: "create_inner",
            });
            return;
        }
        mutationInner.mutate(documentId);
    };



    const handleCreateInvoice = () => {
        if (!document?.all_products_confirmed) {
            antdAsyncMessageError({
                text: "All products must be confirmed before creating invoice",
                key: "create_invoice",
            });
            return;
        }
        mutationInvoice.mutate(documentId);
    };

    const fetchDepartments = async () => {
        setDepartmentsLoading(true);
        try {
            const response = await axios.get('/api/v1/suppliers/for_transfer', {
                params: {
                    per_page: 200,
                },
            });

            const list = response?.data?.data || [];
            const mapped = list.map((supplier) => ({
                value: supplier.id,
                label: supplier.title || supplier.name || supplier.code || `Department ${supplier.id}`,
            }));

            setDepartments(mapped);
            return mapped;
        } catch (error) {
            antdAsyncMessageError({
                text: 'Failed to fetch departments',
                key: 'transfer_departments',
            });
            return [];
        } finally {
            setDepartmentsLoading(false);
        }
    };

    const transferDepartmentIdSet = useMemo(
        () => new Set((departments || []).map((department) => Number(department.value))),
        [departments]
    );

    const buildSourceOptions = (item, allowedDepartmentIds = transferDepartmentIdSet) => {
        const suppliers = item?.product_suppliers || [];

        return suppliers
            .filter((supplier) => allowedDepartmentIds.has(Number(supplier.supplier_id)))
            .filter((supplier) => Number(supplier.supplier_id) !== Number(document?.sender_id))
            .filter((supplier) => (Number(supplier.available_quantity) || 0) > 0)
            .map((supplier) => ({
                value: Number(supplier.supplier_id),
                label: `${supplier.title || supplier.name || `Department ${supplier.supplier_id}`} (${Number(supplier.available_quantity) || 0})`,
            }));
    };

    const canOpenProductTransfer = useMemo(() => {
        return !document?.inner_document_number && document?.status !== 'completed' && document?.status !== 'canceled';
    }, [document?.inner_document_number, document?.status]);

    const selectedRows = useMemo(() => {
        if (!items || items.length === 0 || selected.length === 0) return [];
        return items.filter(item => selectedIdSet.has(String(item.id)));
    }, [items, selected.length, selectedIdSet]);

    const selectedRowsForTransfer = useMemo(() => {
        return selectedRows.filter(item => !item.deleted_at);
    }, [selectedRows]);

    const movableRows = useMemo(() => {
        return selectedRowsForTransfer.filter(item => !item.user_confirmed && !!item.product_id);
    }, [selectedRowsForTransfer]);

    const openTransferModal = async () => {
        if (selected.length === 0) return;

        if (!canOpenProductTransfer) {
            antdAsyncMessageError({
                text: 'Cannot create product transfer for this document status',
                key: 'product_transfer',
            });
            return;
        }

        if (movableRows.length === 0) {
            antdAsyncMessageError({
                text: 'No movable items in selection',
                key: 'product_transfer',
            });
            return;
        }

        setTransferToId(document?.receiver_id || null);
        const mappedDepartments = await fetchDepartments();
        const allowedDepartmentIds = new Set((mappedDepartments || []).map((department) => Number(department.value)));

        const defaultSenderByItem = {};
        movableRows.forEach((item) => {
            const options = buildSourceOptions(item, allowedDepartmentIds);
            if (options.length === 0) {
                return;
            }

            defaultSenderByItem[item.id] = options[0].value;
        });

        setTransferSourceByItemId(defaultSenderByItem);

        setTransferModalOpen(true);
    };

    const closeTransferModal = () => {
        if (transferSubmitting) return;
        setTransferModalOpen(false);
        setTransferToId(null);
        setTransferSourceByItemId({});
    };

    const handleBulkProductTransfer = async () => {
        if (!transferToId) {
            antdAsyncMessageError({
                text: 'Please select receiver department',
                key: 'product_transfer',
            });
            return;
        }

        const transferable = movableRows;
        const skipped = selectedRowsForTransfer.filter(item => item.user_confirmed || !item.product_id);

        if (transferable.length === 0) {
            antdAsyncMessageError({
                text: 'No transferable items selected. Confirmed items are skipped automatically.',
                key: 'product_transfer',
            });
            return;
        }

        setTransferSubmitting(true);

        let transferredCount = 0;
        let failedCount = 0;
        let firstError = null;
        const deletedIds = [];

        for (const item of transferable) {
            try {
                const sourceDepartmentId = transferSourceByItemId[item.id];
                if (!sourceDepartmentId) {
                    throw new Error(`Select source department for ${item.bb_sku || item.title || `item ${item.id}`}`);
                }

                if (Number(sourceDepartmentId) === Number(document?.sender_id)) {
                    throw new Error(`Source department cannot match current document sender for ${item.bb_sku || item.title || `item ${item.id}`}`);
                }

                if (Number(sourceDepartmentId) === Number(transferToId)) {
                    throw new Error(`Source and receiver must be different for ${item.bb_sku || item.title || `item ${item.id}`}`);
                }

                const createResponse = await axios.post('/api/v1/product_transfers/create_item', {
                    sender_id: Number(sourceDepartmentId),
                    receiver_id: Number(transferToId),
                    product_id: item.product_id,
                    quantity: parseInt(item.qty, 10) || 0,
                });

                if (createResponse.data?.success === false) {
                    throw new Error(createResponse.data?.error || 'Failed to create transfer item');
                }

                const deleteResponse = await axios.post(`/api/v1/product_transfers/${documentId}/delete_item`, {
                    product_transfer_item_id: item.id,
                });

                if (deleteResponse.data?.success === false) {
                    throw new Error(deleteResponse.data?.error || 'Failed to mark source item deleted');
                }

                transferredCount += 1;
                deletedIds.push(item.id);
            } catch (error) {
                failedCount += 1;
                if (!firstError) {
                    firstError = error?.response?.data?.error || error?.message || 'Transfer failed for some items';
                }
            }
        }

        const skippedCount = skipped.length;

        if (deletedIds.length > 0) {
            const newDeletedIds = [...localDeletedIds, ...deletedIds];
            setLocalDeletedIds(newDeletedIds);

            const newDeletedIdSet = new Set(newDeletedIds.map((id) => String(id)));
            const activeItemIds = document?.active_item_ids || [];
            const allItemsDeleted = activeItemIds.length > 0 && activeItemIds.every((id) =>
                newDeletedIdSet.has(String(id))
            );

            if (allItemsDeleted && document?.status !== 'canceled') {
                await handleStatusChange('canceled');
            } else {
                await refetchDocument();
            }
        } else {
            await refetchDocument();
        }

        const processedIds = [...deletedIds, ...skipped.map(item => item.id)];
        if (processedIds.length > 0) {
            const processedIdSet = new Set(processedIds.map((id) => String(id)));
            setSelected(prev => prev.filter(id => !processedIdSet.has(String(id))));
        }

        if (transferredCount > 0) {
            antdAsyncMessageSuccess({
                text: `Product transfer completed. Transferred: ${transferredCount}, Skipped: ${skippedCount}, Failed: ${failedCount}`,
                key: 'product_transfer',
            });
        }

        if (failedCount > 0) {
            antdAsyncMessageError({
                text: firstError || `Failed to transfer ${failedCount} item(s)`,
                key: 'product_transfer_error',
            });
        }

        if (transferredCount > 0 || skippedCount > 0) {
            setTransferModalOpen(false);
            setTransferToId(null);
        }

        setTransferSubmitting(false);
    };

    const total_items = useMemo(() => {
        return items?.length || 0;
    }, [items?.length]);

    const filteredData = useMemo(() => {
        if (!items) return [];
        const paginated = paginate(items, pageCount, page);
        return paginated.map(item =>
            localDeletedIdSet.has(String(item.id))
                ? { ...item, deleted_at: item.deleted_at || new Date().toISOString() }
                : item
        );
    }, [page, pageCount, items, localDeletedIdSet]);

    const all_selected = useMemo(() => {
        if (loading) return false;
        if (filteredData.every((d) => selectedIdSet.has(String(d?.id)))) return true;
        return false;
    }, [loading, selectedIdSet, filteredData, page]);

    const totalQty = useMemo(() => {
        if (!items || items.length === 0) return 0;
        return items.reduce((sum, item) => {
            if (item.deleted_at || localDeletedIdSet.has(String(item.id))) return sum;
            return sum + (parseInt(item.qty) || 0);
        }, 0);
    }, [items, localDeletedIdSet]);

    const totalCostPrice = useMemo(() => {
        if (!items || items.length === 0) return 0;
        return items.reduce((sum, item) => {
            if (item.deleted_at || localDeletedIdSet.has(String(item.id))) return sum;
            return sum + (parseFloat(item.cost_price) || 0);
        }, 0);
    }, [items, localDeletedIdSet]);

    const itemsWithQtyMismatch = useMemo(() => {
        if (!items || items.length === 0) return [];
        return items.filter(item => {
            if (item.deleted_at) return false;
            const qty = parseInt(item.qty) || 0;
            const availableQty = parseInt(item.available_qty);
            if (isNaN(availableQty)) return false;
            return qty > availableQty;
        });
    }, [items]);

    return (
        <>
            <ContentBlock>
                <FilterMenu
                    totals={total_items}
                    length={items.length}
                    onChangeCountPage={setPageCount}
                    countPage={pageCount}
                    text2="Products"
                >
                    <InformationNav document={document} />
                </FilterMenu>
            </ContentBlock>

            <ContentBlock>
                <div
                    className="table-responsive"
                    style={{
                        overflowX: window.innerWidth > 1350 ? "visible" : "auto",
                    }}
                >
                    <div style={{ paddingLeft: 12, paddingRight: 12 }} className="row">
                        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center" style={{ gap: 12 }}>
                                <div style={{ width: 125 }}>
                                    <ButtonSecondary onClick={() => {
                                        if (onRefresh && typeof onRefresh === 'function') {
                                            onRefresh();
                                        }
                                        onBack();
                                    }}>
                                        Back
                                    </ButtonSecondary>
                                </div>
                                {selected.length > 0 && (
                                    <DropdownButton
                                        buttonText="Bulk action"
                                        buttonId="bulk-action-doc"
                                        style={{
                                            width: 104,
                                            height: 29,
                                            lineHeight: "19px",
                                            backgroundColor: "#03B0F7",
                                            fontWeight: "600",
                                            padding: "5px 10px",
                                            fontSize: 12,
                                            boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                                            borderRadius: "4px",
                                            border: "none",
                                        }}
                                        child={
                                            <div>
                                                <div
                                                    onClick={() => handleBulkConfirmSelected(true)}
                                                    className="bulk-action-dropbox"
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <p style={{ fontSize: 12, padding: 8, marginBottom: 0, color: "#2B2A29" }}>
                                                        Confirm selected {selected.length}
                                                    </p>
                                                </div>
                                                <div
                                                    onClick={() => handleBulkConfirmSelected(false)}
                                                    className="bulk-action-dropbox"
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <p style={{ fontSize: 12, padding: 8, marginBottom: 0, color: "#2B2A29" }}>
                                                        Unconfirm selected {selected.length}
                                                    </p>
                                                </div>
                                                <div
                                                    onClick={openTransferModal}
                                                    className="bulk-action-dropbox"
                                                    style={{ cursor: canOpenProductTransfer ? "pointer" : "not-allowed", opacity: canOpenProductTransfer ? 1 : 0.5 }}
                                                >
                                                    <p style={{ fontSize: 12, padding: 8, marginBottom: 0, color: "#2B2A29" }}>
                                                        Product transfer {selected.length}
                                                    </p>
                                                </div>
                                                <div
                                                    onClick={() => {
                                                        AntdConfirm({
                                                            title: "Delete selected",
                                                            content: `Are you sure you want to delete ${selected.length} item(s)?`,
                                                            onOk: handleBulkDelete,
                                                        });
                                                    }}
                                                    className="bulk-action-dropbox"
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <p style={{ fontSize: 12, padding: 8, marginBottom: 0, color: "#dc3545" }}>
                                                        Delete selected {selected.length}
                                                    </p>
                                                </div>
                                            </div>
                                        }
                                    />
                                )}
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                {document?.status !== 'completed' && !document?.inner_document_number && !document?.sender_code?.startsWith?.('rivile_') && (
                                    <div style={{ width: 180, paddingRight: 12 }}>
                                        <ButtonSecondary
                                            style={{ backgroundColor: "#1F1A54" }}
                                            disabled={mutationInner.isLoading}
                                            onClick={() => {
                                                AntdConfirm({
                                                    title: "Create Internal Document",
                                                    content: "Are you sure you want to create an internal document?",
                                                    onOk: handleCreateInternalDocument,
                                                });
                                            }}
                                        >
                                            {mutationInner.isLoading ? "Creating..." : "Create internal document"}
                                        </ButtonSecondary>
                                    </div>
                                )}

                                {document?.status !== 'completed' && !document?.invoice_number && document?.sender_code?.startsWith?.('rivile_') && (
                                    <div style={{ width: 180, paddingRight: 12 }}>
                                        <ButtonSecondary
                                            style={{ backgroundColor: "#1F1A54" }}
                                            onClick={() => {
                                                AntdConfirm({
                                                    title: "Create Invoice",
                                                    content: "Are you sure you want to create an invoice?",
                                                    onOk: handleCreateInvoice,
                                                });
                                            }}
                                        >
                                            Create invoice
                                        </ButtonSecondary>
                                    </div>
                                )}

                                {!document?.invoice_number && document?.status !== 'new' && document?.status !== 'completed' && document?.status !== 'canceled' && (
                                    <div style={{ width: 125 }}>
                                        <ButtonSecondary
                                            style={{ backgroundColor: "#E61D01" }}
                                            onClick={() => handleStatusChange('canceled')}
                                        >
                                            Cancel
                                        </ButtonSecondary>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {!document?.inner_document_number && itemsWithQtyMismatch.length > 0 && (
                        <div
                            style={{
                                backgroundColor: '#f8d7da',
                                border: '1px solid #f5c6cb',
                                borderRadius: '4px',
                                padding: '12px 16px',
                                marginBottom: '16px',
                                marginLeft: '12px',
                                marginRight: '12px'
                            }}
                        >
                            <div style={{ fontWeight: 600, color: '#721c24', marginBottom: '8px' }}>
                                Quantity mismatch - the following products exceed available quantity:
                            </div>
                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#721c24' }}>
                                {itemsWithQtyMismatch.map(item => (
                                    <li key={item.id}>
                                        <strong>{item.bb_sku}</strong> - {item.title}:
                                        <span style={{ color: '#dc3545', fontWeight: 600 }}> {item.qty}</span> /
                                        <span style={{ color: '#28a745' }}>{item.available_qty}</span>
                                        <span style={{ fontSize: '12px', marginLeft: '8px' }}>
                                            (missing: {item.qty - item.available_qty})
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <PaginationComp
                        className="mb-0"
                        setPagination={setPage}
                        page={page}
                        totals={total_items}
                        pageCount={pageCount}
                    />

                    <TableUi
                        ths={[
                            <CheckBoxInput
                                checked={all_selected}
                                onChange={() => {
                                    setSelected((prev) => {
                                        const obj = [...prev];
                                        const all_data = filteredData.map((d) => d?.id);
                                        if (all_selected) {
                                            const allDataSet = new Set(all_data.map((id) => String(id)));
                                            return prev.filter((d) => !allDataSet.has(String(d)));
                                        }
                                        const map = new Map();
                                        [...obj, ...all_data].forEach((id) => {
                                            map.set(String(id), Number(id) || id);
                                        });
                                        return Array.from(map.values());
                                    });
                                }}
                            />,
                            "BB SKU",
                            "VV SKU",
                            "Supplier code",
                            "Title",
                            "Category",
                            "Brand",
                            "Cost price",
                            "QTY",
                            <SwitchComp
                                checked={allActiveConfirmed}
                                disabled={!!document?.inner_document_number || document?.status === 'completed' || document?.hasSuccessfulShipment}
                                onChange={handleBulkConfirmToggle}
                            />,
                            "User confirmed",
                            "",
                        ]}
                        total={filteredData?.length || 0}
                    >
                        {filteredData.map((item) => (
                            <TableColumn
                                key={item?.id}
                                {...item}
                                selected={selectedIdSet.has(String(item?.id))}
                                setSelected={setSelected}
                                onConfirmToggle={handleConfirmToggle}
                                documentStatus={document?.status}
                                documentId={documentId}
                                onPriceUpdate={refetchDocument}
                                onDeleteSuccess={async (itemId) => {
                                    const newDeletedIds = [...localDeletedIds, itemId];
                                    setLocalDeletedIds(newDeletedIds);

                                    const newDeletedIdSet = new Set(newDeletedIds.map((id) => String(id)));
                                    const activeItemIds = document?.active_item_ids || [];
                                    const allItemsDeleted = activeItemIds.length > 0 && activeItemIds.every((id) =>
                                        newDeletedIdSet.has(String(id))
                                    );

                                    if (allItemsDeleted && document?.status !== 'canceled') {
                                        await handleStatusChange('canceled');
                                    } else {
                                        refetchDocument();
                                    }
                                }}
                                hasInnerDocument={!!document?.inner_document_number}
                                hasSuccessfulShipment={!!document?.hasSuccessfulShipment}
                            />
                        ))}
                        <tr style={{ fontWeight: 700, borderTop: '1px solid #000' }}>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td>{totalCostPrice.toFixed(2)} &euro;</td>
                            <td>{totalQty}</td>
                            <td></td>
                            <td></td>
                            <td></td>
                        </tr>
                    </TableUi>

                    <PaginationComp
                        className="mb-0"
                        setPagination={setPage}
                        page={page}
                        totals={total_items}
                        pageCount={pageCount}
                    />
                </div>
            </ContentBlock>

            <Modal
                title="Product transfer"
                open={transferModalOpen}
                onCancel={closeTransferModal}
                onOk={handleBulkProductTransfer}
                okText={transferSubmitting ? 'Transferring...' : 'Transfer selected'}
                okButtonProps={{ loading: transferSubmitting, disabled: departmentsLoading || transferSubmitting }}
                cancelButtonProps={{ disabled: transferSubmitting }}
                width={760}
                destroyOnClose
            >
                <div style={{ marginBottom: 12, color: '#6c757d', fontSize: 13 }}>
                    Showing only movable products.
                </div>

                <div className="row" style={{ marginBottom: 12 }}>
                    <div className="col-12">
                        <label style={{ fontWeight: 600, marginBottom: 6, display: 'block' }}>Receiver</label>
                        <Select
                            value={transferToId}
                            onChange={setTransferToId}
                            placeholder="Select receiver"
                            loading={departmentsLoading}
                            options={departments}
                            showSearch
                            optionFilterProp="label"
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>

                <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}>
                    <table className="table mb-0">
                        <thead style={{ position: 'sticky', top: 0, background: '#fafafa', zIndex: 1 }}>
                            <tr>
                                <th>BB SKU</th>
                                <th>VV SKU</th>
                                <th>Title</th>
                                <th>QTY</th>
                                <th>Sender</th>
                            </tr>
                        </thead>
                        <tbody>
                            {movableRows.length === 0 && (
                                <tr>
                                    <td colSpan={5} style={{ textAlign: 'center', color: '#6c757d' }}>
                                        No movable items.
                                    </td>
                                </tr>
                            )}
                            {movableRows.map((item) => {
                                const sourceOptions = buildSourceOptions(item);

                                return (
                                    <tr key={item.id}>
                                        <td>{item.bb_sku}</td>
                                        <td>{item.vv_sku}</td>
                                        <td>{item.title}</td>
                                        <td>{item.qty}</td>
                                        <td style={{ minWidth: 250 }}>
                                            {sourceOptions.length > 0 ? (
                                                <Select
                                                    value={transferSourceByItemId[item.id]}
                                                    onChange={(value) => {
                                                        setTransferSourceByItemId((prev) => ({
                                                            ...prev,
                                                            [item.id]: value,
                                                        }));
                                                    }}
                                                    options={sourceOptions}
                                                    placeholder="Select source"
                                                    showSearch
                                                    optionFilterProp="label"
                                                    disabled={transferSubmitting || departmentsLoading}
                                                    style={{ width: '100%' }}
                                                />
                                            ) : (
                                                <span style={{ color: '#dc3545', fontSize: 12 }}>
                                                    No source departments with stock
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Modal>
        </>
    );
};

const TableColumn = ({
    id,
    bb_sku,
    vv_sku,
    supplier_code,
    title,
    category,
    brand,
    cost_price,
    qty,
    available_qty,
    status,
    user_confirmed,
    confirmed_by,
    reserve_number,
    reserve_error,
    selected,
    setSelected,
    onConfirmToggle,
    documentStatus,
    documentId,
    onPriceUpdate,
    onDeleteSuccess,
    hasInnerDocument,
    hasSuccessfulShipment,
    deleted_at,
}) => {
    const isConfirmed = status === "Confirmed";
    const isDeleted = !!deleted_at;
    const [isEditingPrice, setIsEditingPrice] = useState(false);
    const [editedPrice, setEditedPrice] = useState(cost_price);
    const [isEditingQty, setIsEditingQty] = useState(false);
    const [editedQty, setEditedQty] = useState(qty);
    const [isUpdating, setIsUpdating] = useState(false);

    const handlePriceUpdate = async () => {
        if (!editedPrice || parseFloat(editedPrice) < 0) {
            antdAsyncMessageError({
                text: "Please enter a valid price",
                key: "price_update",
            });
            return;
        }

        setIsUpdating(true);
        try {
            const response = await axios.post(`/api/v1/product_transfers/${documentId}/update_item`, {
                product_transfer_item_id: id,
                prime_cost: parseFloat(editedPrice)
            });

            if (response.data?.success === false) {
                antdAsyncMessageError({
                    text: response.data?.error || "Error updating price",
                    key: "price_update",
                });
                return;
            }

            antdAsyncMessageSuccess({
                text: "Price updated successfully",
                key: "price_update",
            });

            setIsEditingPrice(false);
            if (onPriceUpdate) {
                onPriceUpdate();
            }
        } catch (err) {
            antdAsyncMessageError({
                text: err?.response?.data?.error || "Error updating price",
                key: "price_update",
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCancelEdit = () => {
        setEditedPrice(cost_price);
        setIsEditingPrice(false);
    };

    const handleQtyUpdate = async () => {
        if (!editedQty || parseInt(editedQty) < 1) {
            antdAsyncMessageError({
                text: "Please enter a valid quantity",
                key: "qty_update",
            });
            return;
        }

        setIsUpdating(true);
        try {
            const response = await axios.post(`/api/v1/product_transfers/${documentId}/update_item`, {
                product_transfer_item_id: id,
                quantity: parseInt(editedQty)
            });

            if (response.data?.success === false) {
                antdAsyncMessageError({
                    text: response.data?.error || "Error updating quantity",
                    key: "qty_update",
                });
                return;
            }

            antdAsyncMessageSuccess({
                text: "Quantity updated successfully",
                key: "qty_update",
            });

            setIsEditingQty(false);
            if (onPriceUpdate) {
                onPriceUpdate();
            }
        } catch (err) {
            antdAsyncMessageError({
                text: err?.response?.data?.error || "Error updating quantity",
                key: "qty_update",
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCancelQtyEdit = () => {
        setEditedQty(qty);
        setIsEditingQty(false);
    };

    const handleDeleteItem = async () => {
        if (!confirm("Are you sure you want to delete this product?")) {
            return;
        }

        setIsUpdating(true);
        try {
            const response = await axios.post(`/api/v1/product_transfers/${documentId}/delete_item`, {
                product_transfer_item_id: id
            });

            if (response.data?.success === false) {
                antdAsyncMessageError({
                    text: response.data?.error || "Error deleting product",
                    key: "delete_item",
                });
                return;
            }

            antdAsyncMessageSuccess({
                text: "Product deleted successfully",
                key: "delete_item",
            });

            if (onDeleteSuccess) {
                onDeleteSuccess(id);
            } else if (onPriceUpdate) {
                onPriceUpdate();
            }
        } catch (err) {
            antdAsyncMessageError({
                text: err?.response?.data?.error || "Error deleting product",
                key: "delete_item",
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const canEdit = !isDeleted && !hasSuccessfulShipment && !hasInnerDocument && documentStatus !== 'completed' && !isConfirmed;
    const canDelete = !isDeleted && !isConfirmed;

    // Row background: deleted = grey, confirmed = green, default = transparent
    const rowBackgroundColor = isDeleted ? '#e9ecef' : (isConfirmed ? '#d4edda' : 'transparent');
    const rowStyle = {
        backgroundColor: rowBackgroundColor,
        opacity: isDeleted ? 0.6 : 1,
        textDecoration: isDeleted ? 'line-through' : 'none',
    };

    return (
        <TableUi.tr
            className="align-middle tableColumn"
            style={rowStyle}
        >
            <TableUi.td className="p-left">
                <CheckBoxInput
                    checked={selected}
                    disabled={isDeleted}
                    onChange={() => {
                        if (isDeleted) return;
                        setSelected((prev) => {
                            let obj = [...prev];
                            if (obj.includes(id)) {
                                return obj.filter((d) => d !== id);
                            }
                            obj.push(id);
                            return obj;
                        });
                    }}
                />
            </TableUi.td>
            <TableUi.td className="align-middle">{bb_sku}</TableUi.td>
            <TableUi.td className="align-middle">{vv_sku || "-"}</TableUi.td>
            <TableUi.td className="align-middle">{supplier_code}</TableUi.td>
            <TableUi.td className="align-middle">
                <div className="d-flex flex-wrap-wrap">{title}</div>
            </TableUi.td>
            <TableUi.td className="align-middle">{category}</TableUi.td>
            <TableUi.td className="align-middle">{brand}</TableUi.td>
            <TableUi.td className="align-middle">
                {canEdit ? (
                    isEditingPrice ? (
                        <div className="d-flex align-items-center gap-2">
                            <input
                                type="number"
                                value={editedPrice}
                                onChange={(e) => setEditedPrice(e.target.value)}
                                style={{
                                    width: '80px',
                                    padding: '4px 8px',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                                step="0.01"
                                min="0"
                                disabled={isUpdating}
                            />
                            <div className="d-flex align-items-center">
                                <MdCheck
                                    size={20}
                                    style={{
                                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                                        color: '#28a745',
                                        marginRight: '4px'
                                    }}
                                    onClick={!isUpdating ? handlePriceUpdate : undefined}
                                    title="Accept"
                                />
                                <MdClose
                                    size={20}
                                    style={{
                                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                                        color: '#dc3545'
                                    }}
                                    onClick={!isUpdating ? handleCancelEdit : undefined}
                                    title="Cancel"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex align-items-center">
                            <span>{cost_price} €</span>
                            <MdEdit
                                size={16}
                                style={{
                                    cursor: 'pointer',
                                    marginLeft: '8px',
                                    color: '#03b0f7'
                                }}
                                onClick={() => setIsEditingPrice(true)}
                                title="Edit price"
                            />
                        </div>
                    )
                ) : (
                    <span>{cost_price} €</span>
                )}
            </TableUi.td>
            <TableUi.td className="align-middle">
                {canEdit ? (
                    isEditingQty ? (
                        <div className="d-flex align-items-center gap-2">
                            <input
                                type="number"
                                value={editedQty}
                                onChange={(e) => setEditedQty(e.target.value)}
                                style={{
                                    width: '60px',
                                    padding: '4px 8px',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                                step="1"
                                min="1"
                                disabled={isUpdating}
                            />
                            {!hasInnerDocument && <span>/{available_qty ?? '-'}</span>}
                            <div className="d-flex align-items-center">
                                <MdCheck
                                    size={20}
                                    style={{
                                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                                        color: '#28a745',
                                        marginRight: '4px'
                                    }}
                                    onClick={!isUpdating ? handleQtyUpdate : undefined}
                                    title="Accept"
                                />
                                <MdClose
                                    size={20}
                                    style={{
                                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                                        color: '#dc3545'
                                    }}
                                    onClick={!isUpdating ? handleCancelQtyEdit : undefined}
                                    title="Cancel"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex align-items-center">
                            <span>{qty}{!hasInnerDocument && `/${available_qty ?? '-'}`}</span>
                            <MdEdit
                                size={16}
                                style={{
                                    cursor: 'pointer',
                                    marginLeft: '8px',
                                    color: '#03b0f7'
                                }}
                                onClick={() => setIsEditingQty(true)}
                                title="Edit quantity"
                            />
                        </div>
                    )
                ) : (
                    <span>{qty}{!hasInnerDocument && `/${available_qty ?? '-'}`}</span>
                )}
            </TableUi.td>
            <TableUi.td className="align-middle">
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <SwitchComp
                        className=""
                        checked={status === "Confirmed"}
                        disabled={isDeleted || hasSuccessfulShipment || hasInnerDocument || documentStatus === 'completed'}
                        onChange={() => {
                            onConfirmToggle(id, status === "Confirmed");
                        }}
                    />
                    {reserve_number && (
                        <Tooltip placement="top" title={`Reserve no: ${reserve_number}`}>
                            <span style={{ lineHeight: 0, cursor: 'help' }}>
                                <IconDocNumber fill="#03b0f7" />
                            </span>
                        </Tooltip>
                    )}
                    {reserve_error && (
                        <Tooltip placement="top" title={`Reserve error: ${reserve_error}`}>
                            <span style={{ lineHeight: 0, cursor: 'help', color: '#dc3545' }}>
                                <MdErrorOutline size={16} />
                            </span>
                        </Tooltip>
                    )}
                </div>
            </TableUi.td>
            <TableUi.td className="align-middle">
                <div className="d-flex align-items-center">
                    {confirmed_by && (
                        <span className="text-muted small ml-2">
                            {confirmed_by}
                        </span>
                    )}
                </div>
            </TableUi.td>
            <TableUi.td className="align-middle">
                {canDelete ? (
                    <Tooltip placement="top" title="Delete product">
                        <button
                            className="btn btn-sm"
                            style={{
                                backgroundColor: "#E61D01",
                                padding: '4px 8px',
                                cursor: isUpdating ? 'not-allowed' : 'pointer',
                                opacity: isUpdating ? 0.6 : 1
                            }}
                            onClick={!isUpdating ? handleDeleteItem : undefined}
                            disabled={isUpdating}
                        >
                            <FaTrash className="text-white" style={{ fontSize: 12 }} />
                        </button>
                    </Tooltip>
                ) : (
                    <button
                        className="btn btn-sm"
                        style={{
                            backgroundColor: '#ccc',
                            padding: '4px 8px',
                            cursor: 'not-allowed'
                        }}
                        disabled
                        title="Cannot delete confirmed product"
                    >
                        <FaTrash className="text-white" style={{ fontSize: 12 }} />
                    </button>
                )}
            </TableUi.td>
        </TableUi.tr>
    );
};

const InformationNav = ({ document }) => {
    return (
        <div className="d-flex align-items-center flex-1 justify-content-between">
            <InformationItem
                icon={<IconDocNumber className="text-primary" fill="#03b0f7" />}
                text={"Sup. order No:"}
                subText={document?.document_no}
            />
            <InformationItem
                icon={<MdDateRange className="mr-2 text-primary" size={20} />}
                text={"Arrival date:"}
                subText={document?.arrival_date ? dayjs(document.arrival_date).format("YYYY-MM-DD") : "-"}
            />
            <InformationItem
                icon={<IconWarehouse className="text-primary" fill="#03b0f7" />}
                text={"Sender:"}
                subText={document?.sender}
            />
            <InformationItem
                icon={<IconWarehouse2 className="text-primary" fill="#03b0f7" />}
                text={"Receiver:"}
                subText={document?.receiver}
            />
            <InformationItem
                icon={<IconDocNumber className="text-primary" fill="#03b0f7" />}
                text={"Internal doc. no:"}
                subText={document?.inner_document_number || "-"}
            />
            <InformationItem
                icon={<IconDocNumber className="text-primary" fill="#03b0f7" />}
                text={"Invoice no:"}
                subText={document?.invoice_number || "-"}
            />
        </div>
    );
};

const InformationItem = ({ icon, text, subText }) => {
    return (
        <span className="mx-3">
            <div className="d-flex">
                <div className="d-flex align-items-center mr-2">{icon}</div>
                <div className="text-sm text-black">
                    <div className="font-semi-bold">{text}</div>
                    <p
                        className="mb-0"
                        style={{
                            lineHeight: 0.5,
                        }}
                    >
                        {subText || " - "}
                    </p>
                </div>
            </div>
        </span>
    );
};

export default DocumentDetail; 